import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import {
  InterventionBookingSearchFilter
} from 'components/patient-entry/bookings-table/InterventionBookingsSearchView';
import { InterventionBookings, InterventionBookingSearchRow } from 'components/patient-entry/PatientCentral';
import moment from 'moment';
import React from 'react';
import {
  exportInterventionBookings,
  exportTestInterventionBookings,
  exportVaccinationInterventionBookings,
  getInterventionBookings,
  getTestInterventionBookings,
  getVaccinationInterventionBookings
} from 'services/patientCentralServices';
import {
  InterventionBookingSearchBy,
  InterventionBookingStatus,
  InterventionBookingTestResult,
  TestDateSearchBy
} from 'ts-types/api.enums';
import {
  AnamnesisSeverity,
  InterventionBookingSearchRequest,
  InterventionTestBookingSearchRequest,
  InterventionTestBookingSearchResult,
  InterventionVaccinationBookingSearchRequest
} from 'ts-types/api.types';
import { ISO_DATE_FORMAT } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { isValueEmpty } from 'utils/validatorUtils';

interface State {
  bookingSearchRequest: Partial<InterventionBookingSearchFilter>;
  setBookingSearchRequest: (searchRequest: Partial<InterventionBookingSearchFilter>) => void;

  bookingsLoading: boolean;
  searchBookings: (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                   exportToFile?: boolean) => void;

  searchTestBookingById: (bookingId: number) => Promise<InterventionTestBookingSearchResult | undefined>,

  parentBookingId?: number;
  setParentBookingId: (id?: number) => void;

  selectVaccinationStatus: InterventionBookingStatus;
  setSelectVaccinationStatus: (value: InterventionBookingStatus) => void;

  batchTestVerificationEnabled: boolean;
  selectVerifyTestResults: boolean;
  setSelectVerifyTestResults: (value: boolean) => void;

  canceledInterventionBookingData?: InterventionBookingSearchRow;
  setCanceledInterventionBookingData: (booking?: InterventionBookingSearchRow) => void;
}

const PatientCentralContext = React.createContext({
  bookingSearchRequest: {},
  bookingsLoading: false,
  searchBookings: (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                   exportToFile?: boolean) : void => {},
  searchTestBookingById: async (bookingId: number) : Promise<InterventionTestBookingSearchResult | undefined> => {
    return undefined;
  },
  setBookingSearchRequest: (searchRequest: Partial<InterventionBookingSearchFilter>) : void => {},
  setParentBookingId: (id?: number) : void => {},

  selectVaccinationStatus: InterventionBookingStatus.REQUESTED,
  setSelectVaccinationStatus: (value: InterventionBookingStatus) : void => {},

  batchTestVerificationEnabled: false,
  selectVerifyTestResults: true,
  setSelectVerifyTestResults: (value: boolean) : void => {},

  setCanceledInterventionBookingData: (booking?: InterventionBookingSearchRow) : void => {}
});

interface Props extends JSX.ElementChildrenAttribute, AuthConsumerRenderProps {}

class PatientCentralContextProvider extends React.Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  searchCancelTokenSource? : CancelTokenSource = undefined;

  constructor(props: any) {
    super(props);

    // const startDate = moment().startOf('isoWeek').format(ISO_DATE_FORMAT);
    // const today = moment().endOf('isoWeek').format(ISO_DATE_FORMAT);
    const today = moment().format(ISO_DATE_FORMAT);

    const bookingSearchRequest: Partial<InterventionBookingSearchFilter> = {
      patientQuery: "",
      searchBy: InterventionBookingSearchBy.COVID_TEST,
      testDateSearchBy: TestDateSearchBy.APPOINTMENT_DATE,
      vaccinationDateSearchBy: TestDateSearchBy.APPOINTMENT_DATE,
      inputChannel: "any",
      entryFromDate: today,
      entryToDate: today,
      questionSetId: -1,
      anamnesisSeverity: "any",
      interventionId: -1,
      locationId: -1,
      mandateId: props.currentUser ? props.currentUser.userConfig.mandateId : null,
      interventionFromDate: today,
      interventionToDate: today,
      status: "any",
      resultVerified: "both",
      resultControlled: "both"
    };

    this.state = {
      bookingSearchRequest,
      bookingsLoading: false,
      searchBookings: this.searchBookings,
      searchTestBookingById: this.searchTestBookingById,
      setBookingSearchRequest: this.setBookingSearchRequest,
      setParentBookingId: this.setParentBookingId,
      selectVaccinationStatus: InterventionBookingStatus.REQUESTED,
      setSelectVaccinationStatus: this.setSelectVaccinationStatus,
      batchTestVerificationEnabled: false,
      selectVerifyTestResults: true,
      setSelectVerifyTestResults: this.setSelectVerifyTestResults,
      setCanceledInterventionBookingData: this.setCanceledInterventionBookingData
    };
  }

  componentWillUnmount(): void {
    this.cancelTokenSource.cancel('Component unmounted.');
    if (this.searchCancelTokenSource) {
      this.cancelTokenSource.cancel('Component unmounted.');
    }
  }

  setCanceledInterventionBookingData = (booking?: InterventionBookingSearchRow) => {
    this.setState({
      canceledInterventionBookingData: booking
    });
  };

  setSelectVaccinationStatus = (value: InterventionBookingStatus) => {
    this.setState({
      selectVaccinationStatus: value
    });
  };

  setSelectVerifyTestResults = (value: boolean) => {
    this.setState({
      selectVerifyTestResults: value
    });
  };

  setBookingSearchRequest = (searchRequest: Partial<InterventionBookingSearchFilter>) => {
    this.setState({
      bookingSearchRequest: searchRequest
    });
  }

  setParentBookingId = (id?: number) => {
    this.setState({
      parentBookingId: id
    });
  }

  searchBookings = async (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                          exportToFile?: boolean) => {

    this.setState({
      bookingsLoading: true
    });

    const values = this.state.bookingSearchRequest;

    const searchByEntry = values.searchBy === InterventionBookingSearchBy.ENTRY_DATE;
    const searchByIntervention = values.searchBy === InterventionBookingSearchBy.APPOINTMENT_DATE;
    const searchByTest = values.searchBy === InterventionBookingSearchBy.COVID_TEST;
    const searchByVaccination = values.searchBy === InterventionBookingSearchBy.VACCINATION;

    if (searchByEntry || searchByIntervention) {
      if (values && (
          !isValueEmpty(values.patientQuery)
          || (!isValueEmpty(values.entryFromDate) || !isValueEmpty(values.entryToDate))
          || (!isValueEmpty(values.interventionFromDate) || !isValueEmpty(values.interventionToDate))
          || (!isValueEmpty(values.entryFromAge) || !isValueEmpty(values.entryToAge))
      )) {
        this._searchBookings(onBookingsLoaded, exportToFile);
      }
    } else if (searchByTest) {
      if (values && (
          !isValueEmpty(values.patientQuery) || !isValueEmpty(values.testDateSearchBy)
          || (!isValueEmpty(values.entryFromDate) || !isValueEmpty(values.entryToDate))
      )) {
        this._searchTestBookings(onBookingsLoaded, exportToFile);
      }
    } else if (searchByVaccination) {
      if (values && (
          !isValueEmpty(values.patientQuery)
          || !isValueEmpty(values.vaccinationDateSearchBy)
          || (!isValueEmpty(values.entryFromDate) || !isValueEmpty(values.entryToDate))
          || (!isValueEmpty(values.interventionFromDate) || !isValueEmpty(values.interventionToDate))
          || (!isValueEmpty(values.entryFromAge) || !isValueEmpty(values.entryToAge))
      )) {
        this._searchVaccinationBookings(onBookingsLoaded, exportToFile);
      }
    }
  };

  _searchBookings = async (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                           exportToFile?: boolean) => {

    try {
      if (this.searchCancelTokenSource) {
        this.searchCancelTokenSource.cancel("new search started");
      }
    } catch (e) {
      // do nothing
    }
    this.searchCancelTokenSource = axios.CancelToken.source();

    const {currentUser} = this.props;

    let userMandateId = currentUser && currentUser.userConfig ? currentUser.userConfig.mandateId : undefined;

    const values = this.state.bookingSearchRequest;

    const searchRequest: Partial<InterventionBookingSearchRequest> = {
      patientQuery: values.patientQuery,
      searchBy: values.searchBy,
      mandateId: values.mandateId,
      userMandateId: userMandateId
    };

    const searchByEntryDate = searchRequest.searchBy === InterventionBookingSearchBy.ENTRY_DATE;

    const questionSetId = values.questionSetId;
    if (questionSetId && questionSetId !== -1) {
      searchRequest.questionSetId = questionSetId;
    }

    const anamnesisSeverity = values.anamnesisSeverity;
    if (anamnesisSeverity && anamnesisSeverity !== "any") {
      searchRequest.anamnesisSeverity = anamnesisSeverity as AnamnesisSeverity;
    }

    const status = values.status;
    if (status && status !== "any") {
      searchRequest.status = status;
    }

    if (searchByEntryDate) {
      searchRequest.inputChannel = values.inputChannel != "any" ? values.inputChannel : undefined;

      searchRequest.entryFromDate = moment(values.entryFromDate).format(ISO_DATE_FORMAT);
      searchRequest.entryToDate = moment(values.entryToDate).format(ISO_DATE_FORMAT);

    } else if (searchRequest.searchBy === InterventionBookingSearchBy.APPOINTMENT_DATE) {
      searchRequest.interventionId = values.interventionId != -1 ? values.interventionId : undefined;
      searchRequest.locationId = values.locationId != -1 ? values.locationId : undefined;

      searchRequest.interventionFromDate = moment(values.interventionFromDate).format(ISO_DATE_FORMAT);
      searchRequest.interventionToDate = moment(values.interventionToDate).format(ISO_DATE_FORMAT);
    }

    const onFinally = () => {
      this.setState({
        bookingsLoading: false
      });
    };

    if (exportToFile) {

      exportInterventionBookings(searchRequest, this.cancelTokenSource)
          .catch(noop)
          .finally(onFinally);

    } else {

      getInterventionBookings(searchRequest, this.searchCancelTokenSource)
          .then(response => {
            if (onBookingsLoaded) {
              onBookingsLoaded(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    }
  };

  _searchTestBookings = (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                         exportToFile?: boolean) => {

    try {
      if (this.searchCancelTokenSource) {
        this.searchCancelTokenSource.cancel("new search started");
      }
    } catch (e) {
      // do nothing
    }
    this.searchCancelTokenSource = axios.CancelToken.source();

    const {currentUser} = this.props;
    const values = this.state.bookingSearchRequest;

    const searchRequest: Partial<InterventionTestBookingSearchRequest> = {
      query: values.patientQuery
    };

    searchRequest.mandateId = values.mandateId;
    searchRequest.userMandateId = currentUser!.userConfig.mandateId;

    searchRequest.testDateSearchBy = values.testDateSearchBy;
    searchRequest.inputChannel = values.inputChannel != "any" ? values.inputChannel : undefined;
    searchRequest.companyId = values.companyId ? values.companyId : undefined;
    searchRequest.divisionId = values.divisionId ? values.divisionId : undefined;
    searchRequest.testKitCode = values.testKitCode ? values.testKitCode : undefined;
    searchRequest.entryFromDate = moment(values.entryFromDate).format(ISO_DATE_FORMAT);
    searchRequest.entryToDate = moment(values.entryToDate).format(ISO_DATE_FORMAT);
    searchRequest.interventionId = values.interventionId != -1 ? values.interventionId : undefined;
    searchRequest.locationId = values.locationId != -1 ? values.locationId : undefined;
    searchRequest.productId = values.productId;
    searchRequest.urgency = values.urgency ? values.urgency : undefined;

    const status = values.status;
    if (status && status !== "any") {
      searchRequest.status = status;
    }

    let testResults: Array<string> = Object.values(InterventionBookingTestResult);
    testResults.push("WITHOUT_RESULT");

    if (values.testResult && testResults.includes(values.testResult)) {
      searchRequest.testResult = values.testResult;
    }

    searchRequest.resultVerified = values.resultVerified;
    searchRequest.resultControlled = values.resultControlled;
    searchRequest.poolFilter = values.poolFilter;

    const onFinally = () => {
      this.setState({
        bookingsLoading: false
      });
    };

    if (exportToFile) {
      exportTestInterventionBookings(searchRequest, this.cancelTokenSource)
          .catch(noop)
          .finally(onFinally)
    } else {
      getTestInterventionBookings(searchRequest, this.searchCancelTokenSource)
          .then(response => {
            if (onBookingsLoaded) {
              onBookingsLoaded(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    }
  };

  _searchVaccinationBookings = (onBookingsLoaded?: (bookings: InterventionBookings) => void,
                                exportToFile?: boolean) => {

    try {
      if (this.searchCancelTokenSource) {
        this.searchCancelTokenSource.cancel("new search started");
      }
    } catch (e) {
      // do nothing
    }
    this.searchCancelTokenSource = axios.CancelToken.source();

    const {currentUser} = this.props;

    let userMandateId = currentUser && currentUser.userConfig ? currentUser.userConfig.mandateId : undefined;

    const values = this.state.bookingSearchRequest;

    const searchRequest: Partial<InterventionVaccinationBookingSearchRequest> = {
      vaccinationDateSearchBy: values.vaccinationDateSearchBy,
      patientQuery: values.patientQuery,
      mandateId: values.mandateId,
      userMandateId: userMandateId,
      searchBy: values.searchBy
    };

    const questionSetId = values.questionSetId;
    if (questionSetId && questionSetId !== -1) {
      searchRequest.questionSetId = questionSetId;
    }

    const status = values.status;
    if (status && status !== "any") {
      searchRequest.status = status;
    }

    searchRequest.inputChannel = values.inputChannel != "any" ? values.inputChannel : undefined;
    searchRequest.entryFromDate = moment(values.entryFromDate).format(ISO_DATE_FORMAT);
    searchRequest.entryToDate = moment(values.entryToDate).format(ISO_DATE_FORMAT);
    searchRequest.interventionId = values.interventionId != -1 ? values.interventionId : undefined;
    searchRequest.entryFromAge = values.entryFromAge;
    searchRequest.entryToAge = values.entryToAge;

    const onFinally = () => {
      this.setState({
        bookingsLoading: false
      });
    };


    if (exportToFile) {

      exportVaccinationInterventionBookings(searchRequest, this.cancelTokenSource)
          .catch(noop)
          .finally(onFinally);

    } else {

      getVaccinationInterventionBookings(searchRequest, this.searchCancelTokenSource)
          .then(response => {
            if (onBookingsLoaded) {
              onBookingsLoaded(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    }
  }

  searchTestBookingById = async (bookingId: number) : Promise<InterventionTestBookingSearchResult | undefined> => {

    const {currentUser} = this.props;

    const userMandateId = currentUser!.userConfig.mandateId;

    const searchRequest: Partial<InterventionTestBookingSearchRequest> = {
      bookingId: bookingId,
      userMandateId: userMandateId
    };

    const res = await getTestInterventionBookings(searchRequest, this.cancelTokenSource);
    return res && res.length > 0 ? res[0] : undefined;
  }

  render() {
    return (
        <PatientCentralContext.Provider value={this.state}>
          {this.props.children}
        </PatientCentralContext.Provider>
    )
  }
}

export const PatientCentralContextProviderWrapper = withAuthContext(PatientCentralContextProvider);

export const PatientCentralConsumer = PatientCentralContext.Consumer;

export interface PatientCentralConsumerRenderProps extends State {
}

export const withPatientCentralContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <PatientCentralConsumer>
        {({...patientEntryProps}: PatientCentralConsumerRenderProps) => {
          return <Component {...props} {...patientEntryProps} />
        }}
      </PatientCentralConsumer>
  );
};
